import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
// For now we're not using Apollo mutations
// import { useMutation } from "@apollo/client";
// import { UPDATE_FORM_FIELDS } from "./graphql/operations";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragHandle as DragHandleIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";

// Styled components
const Card = styled(MuiCard)(spacing);

const FieldItem = styled(Box)`
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  margin-bottom: 16px;
  background-color: ${(props) => props.theme.palette.background.paper};
  position: relative;
  &:hover {
    box-shadow: 0 0 0 1px ${(props) => props.theme.palette.primary.main};
  }
`;

const FieldContent = styled(Box)`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DragHandle = styled(Box)`
  display: flex;
  align-items: center;
  padding: 8px;
  cursor: grab;
  color: #999;
`;

function FieldsTab({ form, setForm }) {
  const [fields, setFields] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [hasChanges, setHasChanges] = useState(false);

  // State for tracking loading status
  const [updateLoading, setUpdateLoading] = useState(false);

  // Field type options
  const fieldTypes = [
    { value: "SHORT_TEXT", label: "Short Text" },
    { value: "LONG_TEXT", label: "Long Text" },
    { value: "NUMBER", label: "Number" },
    { value: "SINGLE_SELECT", label: "Single Select" },
    { value: "MULTI_SELECT", label: "Multi Select" },
    { value: "EMAIL", label: "Email" },
    { value: "PHONE", label: "Phone" },
    { value: "CHECKBOX", label: "Checkbox" },
    { value: "RADIO_GROUP", label: "Radio Group" },
    { value: "ADDRESS", label: "Address" },
  ];

  // Load fields from form
  useEffect(() => {
    console.log("FieldsTab: form prop changed:", form);
    console.log("FieldsTab: form.fields =", form?.fields);

    if (form && form.fields && Array.isArray(form.fields)) {
      console.log("FieldsTab: Setting fields from form.fields:", form.fields);
      setFields(form.fields);
    } else if (form) {
      // Initialize with empty fields array if not present
      console.log("FieldsTab: No fields in form, initializing empty array");
      setFields([]);
    }
  }, [form]);

  // Handle drag end
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFields(items);
    setHasChanges(true);
  };

  // Handle add new field
  const handleAddField = () => {
    // Generate a unique ID by using timestamp + random number
    const uniqueId = `f_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

    setCurrentField({
      id: uniqueId,
      type: "SHORT_TEXT",
      label: "",
      identifier: "",
      placeholder: "",
      required: false,
      options: [],
    });
    setOpenDialog(true);
  };

  // Handle edit field
  const handleEditField = (field) => {
    setCurrentField({ ...field });
    setOpenDialog(true);
  };

  // Handle delete field
  const handleDeleteField = (fieldId) => {
    setFields(fields.filter((field) => field.id !== fieldId));
    setHasChanges(true);
  };

  // Handle save field
  const handleSaveField = () => {
    if (currentField.id && fields.some((f) => f.id === currentField.id)) {
      // Update existing field
      setFields(
        fields.map((field) =>
          field.id === currentField.id ? currentField : field
        )
      );
    } else {
      // Add new field
      setFields([...fields, currentField]);
    }
    setOpenDialog(false);
    setHasChanges(true);
  };

  // Handle field change
  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    // Auto-generate a snake_case identifier from the label if it's empty
    if (
      name === "label" &&
      (!currentField.identifier || currentField.identifier === "")
    ) {
      const generatedIdentifier = value
        .toLowerCase()
        .replace(/[^a-z0-9 ]/g, "") // Remove special characters
        .replace(/\s+/g, "_"); // Replace spaces with underscores

      setCurrentField({
        ...currentField,
        label: value,
        identifier: generatedIdentifier,
      });
    } else {
      setCurrentField({
        ...currentField,
        [name]: value,
      });
    }
  };

  // Handle required toggle
  const handleRequiredChange = (e) => {
    setCurrentField({
      ...currentField,
      required: e.target.value === "true",
    });
  };

  // Handle adding an option to a select/radio/checkbox field
  const handleAddOption = () => {
    setCurrentField({
      ...currentField,
      options: [...(currentField.options || []), "New Option"],
    });
  };

  // Handle option change
  const handleOptionChange = (index, value) => {
    const newOptions = [...currentField.options];
    newOptions[index] = value;
    setCurrentField({
      ...currentField,
      options: newOptions,
    });
  };

  // Handle removing an option
  const handleRemoveOption = (index) => {
    const newOptions = [...currentField.options];
    newOptions.splice(index, 1);
    setCurrentField({
      ...currentField,
      options: newOptions,
    });
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  // Save all fields to the backend
  const handleSaveAllFields = async () => {
    // Check for Form ID
    if (!form?._id) {
      // Try using id if _id is not available (compatibility)
      if (!form?.id) {
        setNotification({
          open: true,
          message: "Form ID is missing. Cannot save fields.",
          severity: "error",
        });
        return;
      }
    }

    const formId = form._id || form.id;

    try {
      // Show loading state
      setUpdateLoading(true);

      // Import axiosInstance for API call
      const { axiosInstance } = await import("../../../utils/axios");

      // Call the backend to update the form with the new fields
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation UpdateRoutingForm($id: ID!, $input: RoutingFormInput!) {
            updateRoutingForm(id: $id, input: $input) {
              _id
              name
              description
              status
              fields
              routes
              fallback
              emailSettings
              createdAt
              updatedAt
            }
          }
        `,
        variables: {
          id: formId,
          input: {
            name: form.name, // name is required
            fields: fields,
          },
        },
      });

      console.log("Update fields response:", response.data);

      const result =
        response.data?.data?.updateRoutingForm ||
        response.data?.updateRoutingForm;

      if (result) {
        // Update the parent component's state with the server response
        setForm({
          ...form,
          ...result,
          // Parse JSON fields if they are strings
          fields:
            typeof result.fields === "string"
              ? JSON.parse(result.fields)
              : result.fields || fields,
          routes:
            typeof result.routes === "string"
              ? JSON.parse(result.routes)
              : result.routes || form.routes,
          fallback:
            typeof result.fallback === "string"
              ? JSON.parse(result.fallback)
              : result.fallback || form.fallback,
          emailSettings:
            typeof result.emailSettings === "string"
              ? JSON.parse(result.emailSettings)
              : result.emailSettings || form.emailSettings,
        });

        setNotification({
          open: true,
          message: "Fields saved successfully",
          severity: "success",
        });

        setHasChanges(false);
      } else {
        throw new Error("No data returned from server");
      }
    } catch (error) {
      console.error("Error updating fields:", error);
      setNotification({
        open: true,
        message: `Error updating fields: ${error.message}`,
        severity: "error",
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  // Get a display name for field types
  const getFieldTypeName = (type) => {
    const fieldType = fieldTypes.find((ft) => ft.value === type);
    return fieldType ? fieldType.label : type;
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4" gutterBottom>
          Fields
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveAllFields}
          disabled={updateLoading || !hasChanges}
        >
          {updateLoading ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      <Typography variant="body2" paragraph>
        Configure what information should be captured during form submission
      </Typography>

      {updateLoading && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="fields">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ minHeight: 100 }}
            >
              {fields.length === 0 && (
                <Box
                  p={3}
                  mb={3}
                  textAlign="center"
                  bgcolor="action.hover"
                  borderRadius={1}
                >
                  <Typography variant="body1" color="textSecondary">
                    No fields added yet. Add fields to capture information from
                    your users.
                  </Typography>
                </Box>
              )}

              {fields.map((field, index) => (
                <Draggable key={field.id} draggableId={field.id} index={index}>
                  {(provided) => (
                    <FieldItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <FieldContent>
                        <Box display="flex" alignItems="center" width="100%">
                          <DragHandle {...provided.dragHandleProps}>
                            <DragHandleIcon />
                          </DragHandle>
                          <Box ml={2} flex={1}>
                            <Typography variant="subtitle1">
                              {field.label || <em>No Label</em>}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {field.identifier ? `(${field.identifier})` : ""}{" "}
                              {getFieldTypeName(field.type)}
                            </Typography>
                          </Box>
                          {field.required && (
                            <Chip
                              label="Required"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ marginRight: 2 }}
                            />
                          )}
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => handleEditField(field)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteField(field.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </FieldContent>
                    </FieldItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Box mt={2}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddField}
          fullWidth
        >
          Add New Field
        </Button>
      </Box>

      {/* Add/Edit Field Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentField?.id && fields.some((f) => f.id === currentField.id)
            ? "Edit Field"
            : "Add Field"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Field Type Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Field Configuration
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <TextField
                  select
                  label="Field Type"
                  name="type"
                  value={currentField?.type || ""}
                  onChange={handleFieldChange}
                  helperText="Choose the type of input field"
                  variant="outlined"
                >
                  <MenuItem value="" disabled>
                    Select field type
                  </MenuItem>
                  {fieldTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Box>

            {/* Basic Information Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
              >
                <TextField
                  fullWidth
                  label="Label"
                  name="label"
                  value={currentField?.label || ""}
                  onChange={handleFieldChange}
                  helperText="The text shown to the user filling out the form"
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  label="Identifier"
                  name="identifier"
                  value={currentField?.identifier || ""}
                  onChange={handleFieldChange}
                  helperText="Used to identify this field in rules and responses (auto-generated from label if empty)"
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  label="Placeholder"
                  name="placeholder"
                  value={currentField?.placeholder || ""}
                  onChange={handleFieldChange}
                  helperText="Optional text that appears in the field when empty"
                  variant="outlined"
                />
              </Box>
            </Box>

            {/* Field Settings Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Field Settings
              </Typography>
              <Box sx={{ mt: 2 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ mb: 1 }}>
                    Is this field required?
                  </FormLabel>
                  <RadioGroup
                    row
                    name="required"
                    value={currentField?.required ? "true" : "false"}
                    onChange={handleRequiredChange}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Yes, required"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="No, optional"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            {/* Options for select, radio, and checkbox fields */}
            {currentField &&
              ["SINGLE_SELECT", "MULTI_SELECT", "RADIO_GROUP"].includes(
                currentField.type
              ) && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Field Options
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    Add the options users can select from
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {currentField.options &&
                      currentField.options.map((option, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            value={option}
                            onChange={(e) =>
                              handleOptionChange(index, e.target.value)
                            }
                            placeholder={`Option ${index + 1}`}
                            variant="outlined"
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveOption(index)}
                            color="error"
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}

                    {(!currentField.options ||
                      currentField.options.length === 0) && (
                      <Box
                        sx={{
                          py: 2,
                          textAlign: "center",
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          No options added yet
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleAddOption}
                        startIcon={<AddIcon />}
                        size="small"
                      >
                        Add Option
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveField}
            color="primary"
            disabled={!currentField?.label || !currentField?.identifier}
          >
            Save Field
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FieldsTab;
